/**
 * BudgetExceededOverlay
 *
 * Full-screen blocking overlay shown when the `budgetExceeded` Remote Config
 * flag is on — video playback, video calls and LIVE streaming are stopped.
 *
 * Flutter source of truth: `AppOverlay.budgetExceededOverlay()`
 * (`overlay_widget.dart:14-110`) — a blurred 50 %-alpha scrim (`:24-36`), a
 * centred title (`:41-49`), the body copy (`:50-60`), and a red "Close" button
 * that removes the overlay (`:61-98`; removal at `:70-73`). The web equivalent
 * of that removal is dispatching `hideBudgetExceededOverlay()` — this Redux
 * flag *is* the visibility state (see the slice header for why Flutter resets
 * its notifier flag immediately and the web does not).
 *
 * Copy note: Flutter's body text carries two user-visible typos — "therefor"
 * (`:54`) and "Subcriptions" (`:55`). Per `conventions.md` → "Handling obvious
 * Flutter bugs" the web ships the corrected spelling; this copy is out of scope
 * for the Flutter edits this branch does make, so it stays a "bug found on
 * Flutter, web implemented correctly" case.
 */

import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  ExpauseButton,
  ButtonSize,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { AppDispatch } from '@/presentation/store';
import { hideBudgetExceededOverlay } from '@/presentation/store/slices/appOverlay/appOverlaySlice';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { AppOverlayShell } from './AppOverlayShell';

export const BudgetExceededOverlay: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const onClose = useCallback(() => {
    dispatch(hideBudgetExceededOverlay());
  }, [dispatch]);

  return (
    <AppOverlayShell
      qaId="budget-exceeded-overlay"
      // Flutter scrim: `backgroundColorLight.withValues(alpha: .5)` (`:28-30`).
      scrimColor={theme.modalOverlay}
      ariaLabel={l10n.appOverlay.budgetExceededTitle}
    >
      <h2
        style={{
          // Flutter `AppStyles.overlayTitleStyle` — 20 px, normal weight.
          fontSize: responsive.font['2xl'],
          fontWeight: responsive.fontWeight.regular,
          color: theme.text.primary,
          margin: 0,
        }}
      >
        {l10n.appOverlay.budgetExceededTitle}
      </h2>

      <p
        style={{
          // Flutter `AppStyles.overlaySubTitleStyle` — 16 px, normal weight.
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
          color: theme.text.primary,
          margin: 0,
          // The copy carries Flutter's own `\n` line break (`:51-52`).
          whiteSpace: 'pre-line',
        }}
      >
        {l10n.appOverlay.budgetExceededMessage}
      </p>

      {/* Flutter's red "Close" button (`:72-92`, `AppColors.activeRed`) maps to
          the shared danger variant. Width capped like the delete-account page's
          destructive control (`DeleteAccountPage.tsx:227-237`) so it does not
          stretch the full column on desktop. */}
      <ExpauseButton
        {...qaAttr({ id: 'budget-exceeded-close' })}
        variant={ButtonVariant.danger}
        size={ButtonSize.large}
        className="w-full max-w-sm"
        onClick={onClose}
      >
        {l10n.common.close}
      </ExpauseButton>
    </AppOverlayShell>
  );
};
